import React, { useCallback, useMemo } from 'react';

// Globals
import { useDispatch, useSelector } from 'react-redux';

// UI
import { Col, Input, Popover } from 'antd';
import deleteIcon from '../../studio/assets/feedcardIcons/deleteIcon.png';

// Locals
import { addCardProps } from '../../actions';
import {
  defaultImageEditorProps,
  formInputLength,
  handleInputChange,
  isUrlHttps,
  urlRegex,
  urlWithoutHttps,
} from '../utils';
import { CardFormField } from './CardFormField';

// Types
import type { IState, SocialUrls, FeedCard } from '../../graphql/types';

// UTILS

import styles from './CardForm.module.css';
import ImageEditor from '../ImageEditor/ImageEditor';
import useFeedCardForm from '../../hooks/useFeedCardForm';
import { ImageEditorProps } from '../../types';

const ProfileForm: React.FC<{
  validationFailed: boolean;
  saving: boolean;
  editorRef;
  setEditorRef;
}> = ({ validationFailed, saving, editorRef, setEditorRef }) => {
  const dispatch = useDispatch();

  const fullName = useSelector<IState, string | undefined>(state =>
    [state.cardProps?.firstName, state.cardProps?.lastName]
      .filter(o => typeof o === 'string')
      .join(' ')
  );

  const expertise = useSelector<IState, string | undefined>(
    state => state.cardProps?.expertise as string | undefined
  );

  const socialUrls = useSelector<IState, SocialUrls | undefined>(
    state => state.cardProps?.socialUrls
  );

  const cardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.cardProps
  );

  const { handleRemove, updateImageCardProps } = useFeedCardForm(
    cardProps?.type
  );

  const newImgLoadedBlob = useSelector<IState, string | undefined>(
    state => state.newImgLoadedBlob
  );

  const processUrl = (value: string): string => {
    const matched = value.match(/^(https?:\/\/)?(.+)$/);
    const prefix = matched ? (matched[1] ? matched[1] : 'https://') : '';
    const newUrl = matched ? matched[2] : value;
    return prefix + newUrl;
  };

  const updateSocialUrls = (
    url: string,
    type: 'twitter' | 'linkedin' | 'website'
  ) => {
    let { linkedin, twitter, website } = socialUrls ?? {};
    url = processUrl(url);
    if (isUrlHttps(url) || url === '') {
      if (type === 'linkedin') linkedin = url;
      else if (type === 'twitter') twitter = url;
      switch (type) {
        case 'linkedin':
          linkedin = url;
          break;
        case 'twitter':
          twitter = url;
          break;
        case 'website':
          website = url;
          break;
      }
      dispatch(
        addCardProps('socialUrls', {
          twitter,
          linkedin,
          website,
        })
      );
    }
  };

  const urlValidator = e => {
    const matched = e.target.value.match(/^(https:\/\/)(.+)$/);
    return matched?.length > 2 ? matched[2] : e.target.value;
  };

  const urlValidationTooltip = title => (
    <Popover
      placement="topLeft"
      open={!!cardProps}
      title={title}
      overlayClassName="urlValidationTooltip"
      align={{ offset: [140, -30] }}
    />
  );

  const urlRules = [
    {
      message: urlValidationTooltip('Only https address is allowed'),
      pattern: /^(?!http:\/\/)(.+)$/,
      validateTrigger: 'onBlur',
    },
    {
      message: urlValidationTooltip('Incorrect url format'),
      pattern: urlRegex,
      validateTrigger: 'onBlur',
    },
  ];

  const handleLoadImage = useCallback(
    file => {
      updateImageCardProps(file, true);
    },
    [updateImageCardProps]
  );

  const imageUrl = useMemo(() => {
    if (cardProps?.customImage) {
      if (cardProps?.uploadedImgBlob as string) {
        return cardProps?.imageBlob as string;
      } else if (cardProps?.imageBlob) {
        return cardProps?.imageBlob;
      }
    }

    return null;
  }, [
    cardProps?.customImage,
    cardProps?.uploadedImgBlob,
    cardProps?.imageBlob,
  ]);

  const imageEditorProps = useMemo<ImageEditorProps>(
    () => ({
      ...defaultImageEditorProps,
      image: imageUrl,
      fileName: cardProps?.customImageName || 'unnamed_file',
      handleRemove,
      handleLoadImage,
      editorRef,
      updateImageCardProps,
      hideUploadIcon: true,
      setEditorRef,
      disabled: saving,
      imgBorderRadius: 80,
      staticImageClass: styles.staticImageRounded,
      validationFailed,
      buttons: (
        <div className={styles.fcIcons}>
          <div
            onClick={() => {
              handleRemove();
            }}
            className={styles.deleteIconWrapper}
          >
            <img
              className={styles.deleteIcon}
              src={deleteIcon}
              alt="delete icon"
            />
          </div>
        </div>
      ),
      text: (
        <div style={{ padding: '30pt' }}>
          <div>
            <span className={styles.imgEditorUnderlineText}>
              Upload an image
            </span>{' '}
            or drag it here
          </div>
          <div>
            <span className={styles.imgEditorMutedText}>
              PNGs, JPGs, or GIFs up to 10MB
            </span>
          </div>
        </div>
      ),
    }),
    [
      cardProps,
      editorRef,
      handleRemove,
      handleLoadImage,
      validationFailed,
      saving,
      updateImageCardProps,
      setEditorRef,
      imageUrl,
    ]
  );

  return (
    <div className={styles.formWrapper}>
      <Col span={24} style={{ display: 'flex', placeContent: 'center' }}>
        <div className={styles.imageEditorWrapper}>
          <ImageEditor props={imageEditorProps} />
        </div>
      </Col>

      <CardFormField
        key={'name'}
        name="profileFullName"
        fieldLabel="Name"
        errorName={'name'}
        boldLabel={true}
        defaultValue={fullName}
        customValidator={e => formInputLength(e.target.value, 60)}
        customRules={[{ max: 60, message: 'Limit of 60 characters exceeded' }]}
      >
        <Input
          value={fullName}
          autoFocus={!newImgLoadedBlob}
          className="modal-form-input"
          autoComplete={'off'}
          onChange={handleInputChange(value => {
            const names = value.split(' ');
            const firstName = names.shift();
            const lastName = names.length > 0 ? names.join(' ') : null;
            dispatch(
              addCardProps(
                'firstName',
                typeof firstName === 'string' ? firstName : null
              )
            );
            dispatch(
              addCardProps(
                'lastName',
                typeof lastName === 'string' ? lastName : null
              )
            );
          })}
          disabled={saving}
          placeholder="Frida Kahlo"
        />
      </CardFormField>

      <CardFormField
        key={'expertise'}
        name="profileExpertise"
        fieldLabel="Expertise or title"
        errorName={'expertise or title'}
        boldLabel={true}
        defaultValue={expertise}
        customValidator={e => formInputLength(e.target.value, 40)}
        customRules={[{ max: 40, message: 'Limit of 40 characters exceeded' }]}
      >
        <Input
          value={expertise}
          className="modal-form-input"
          onChange={handleInputChange(value =>
            dispatch(addCardProps('expertise', value))
          )}
          disabled={saving}
          autoComplete={'off'}
          placeholder="Painter"
        />
      </CardFormField>

      <CardFormField
        key={'X'}
        name="profileTwitter"
        boldLabel={true}
        fieldLabel="X profile url (optional)"
        defaultValue={
          socialUrls?.twitter ? urlWithoutHttps(socialUrls?.twitter) : ''
        }
        required={false}
        customValidator={urlValidator}
        customRules={urlRules}
      >
        <Input
          value={
            socialUrls?.twitter ? urlWithoutHttps(socialUrls?.twitter) : ''
          }
          addonBefore={'https://'}
          autoComplete={'off'}
          className="modal-form-input"
          onChange={e => updateSocialUrls(e.target.value, 'twitter')}
          disabled={saving}
          placeholder="X url"
        />
      </CardFormField>

      <CardFormField
        key={'linkedin'}
        name="profileLinkedin"
        fieldLabel="Linkedin profile url (optional)"
        boldLabel={true}
        defaultValue={
          socialUrls?.linkedin ? urlWithoutHttps(socialUrls?.linkedin) : ''
        }
        required={false}
        customValidator={urlValidator}
        customRules={urlRules}
      >
        <Input
          value={
            socialUrls?.linkedin ? urlWithoutHttps(socialUrls?.linkedin) : ''
          }
          addonBefore={'https://'}
          autoComplete={'off'}
          className="modal-form-input"
          onChange={e => updateSocialUrls(e.target.value, 'linkedin')}
          disabled={saving}
          placeholder="Linkedin url"
        />
      </CardFormField>
    </div>
  );
};

export default ProfileForm;
